/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import { IFilterDto } from '../model/Core.model'
import './TagInputSearchControls.css'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import useDebouncedValue from '../../util/CustomHooks'

interface Props {
  loading: boolean
  enabled: boolean
  filterKeys: {
    defaultKey: string
    defaultKeyList: string[]
  }
  filterSuggestions: string[]
  filters: IFilterDto[]
  createOnClickPath: string
  createOnClickOpenNew?: boolean
  setFilterSuggestions: (filterSuggestions) => void
  setFilters: (filters) => void
  searchOnClickHandler: () => void
  resetSearchResetOnClickHandler: () => void
}

const TagInputSearchListControls: FC<Props> = ({
  loading,
  enabled,
  filterKeys,
  filterSuggestions,
  filters,
  createOnClickPath,
  createOnClickOpenNew = true,
  setFilterSuggestions,
  setFilters,
  searchOnClickHandler,
  resetSearchResetOnClickHandler,
}: Props) => {
  const intl = useIntl()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [searchValue, setSearchValue] = useState('')
  const [searchValueHidden, setSearchValueHidden] = useState('')
  const [isValidTagList, setIsValidTagList] = useState<boolean>(false)

  useEffect(() => {
    setIsValidTagList((!searchValue || searchValue.trim() === '') && filters.length > 0)
  })

  const searchBarOnChangeHandler = (event) => {
    if (event.target.value === '') {
      setSearchValue('')
      setIsValidTagList(filters.length > 0)
    } else {
      setSearchValue(event.target.value)
      setIsValidTagList(false)
    }
  }

  const mainOnClickHandler = () => {
    if (isValidTagList) {
      // Execute the search action
      setSearchValue('')
      searchOnClickHandler()
    } else {
      // Parse the search value and set the filters
      const isValid = processSearchHandler(searchValue)
      setIsValidTagList(isValid)
    }
  }

  const searchBarOnKeyDownHandler = (e) => {
    if (e.key !== 'Enter') {
      return
    }
    e.preventDefault()
    const isValid = processSearchHandler(searchValue)
    setIsValidTagList(isValid)
  }

  const processSearchHandler = (value): boolean => {
    if (!value || !value.trim()) {
      if (filters.length > 0) {
        mainOnClickHandler()
        return false
      }
      return false
    }
    const valueTokens: string[] = value.trim().split('=')
    if (valueTokens.length == 1) {
      //No filter key provided, using default
      if (!valueTokens[0] || valueTokens[0].length === 0) {
        return false;
      }
      const tokenValue = valueTokens[0].trim().toUpperCase()
      if (filters.length > 0) {
        //Default filter key used, replacing it
        const newFilters = filters.filter((filter) => filter.id !== filterKeys.defaultKey)
        setFilters([...newFilters, {id: filterKeys.defaultKey, value: tokenValue, editable: true}])
        setSearchValue('')
        setSearchValueHidden('')
        return true;
      }
      //Default filter key NOT used, setting default filter key
      const newFilters = [
        ...filters,
        {id: filterKeys.defaultKey, value: tokenValue, editable: true},
      ]
      setFilters(newFilters)
      setSearchValue('')
      setSearchValueHidden('')
      setFilterSuggestions(
        filterKeys.defaultKeyList.filter(
          (defaultKey) => !newFilters.map((filter) => filter.id).includes(defaultKey)
        )
      )
      return true;
    } else {
      const tokenKey = valueTokens[0].trim().toUpperCase()
      const tokenValue = valueTokens[1].trim().toUpperCase()
      if (!searchValueHidden) {
        if (
          !filterKeys.defaultKeyList.includes(tokenKey) ||
          !valueTokens[1] ||
          typeof valueTokens[1] !== 'string' ||
          valueTokens[1].length === 0
        ) {
          return false;
        }
        if (filters.length > 0) {
          //Adding or replacing key
          const newFilters = [
            ...filters.filter((filter) => filter.id !== tokenKey),
            {id: tokenKey, value: tokenValue, editable: true},
          ]
          setFilters(newFilters)
          setSearchValue('')
          setSearchValueHidden('')
          setFilterSuggestions(
            filterKeys.defaultKeyList.filter(
              (defaultKey) => !newFilters.map((filter) => filter.id).includes(defaultKey)
            )
          )
          return true;
        }
        const newFilters = [...filters, {id: tokenKey, value: tokenValue, editable: true}]
        setFilters(newFilters)
        setSearchValue('')
        setSearchValueHidden('')
        setFilterSuggestions(
          filterKeys.defaultKeyList.filter(
            (defaultKey) => !newFilters.map((filter) => filter.id).includes(defaultKey)
          )
        )
        return true;
      } else {
        if (
          !filterKeys.defaultKeyList.includes(searchValueHidden) ||
          !valueTokens[1] ||
          typeof valueTokens[1] !== 'string' ||
          valueTokens[1].length === 0
        ) {
          return false;
        }
      }
      if (filters.length > 0) {
        //Adding or replacing key
        const newFilters = [
          ...filters.filter((filter) => filter.id !== searchValueHidden),
          {id: searchValueHidden, value: tokenValue, editable: true},
        ]
        setFilters(newFilters)
        setSearchValue('')
        setSearchValueHidden('')
        setFilterSuggestions(
          filterKeys.defaultKeyList.filter(
            (defaultKey) => !newFilters.map((filter) => filter.id).includes(defaultKey)
          )
        )
        return true;
      }
      const newFilters = [...filters, {id: searchValueHidden, value: tokenValue, editable: true}]
      setFilters(newFilters)
      setSearchValue('')
      setSearchValueHidden('')
      setFilterSuggestions(
        filterKeys.defaultKeyList.filter(
          (defaultKey) => !newFilters.map((filter) => filter.id).includes(defaultKey)
        )
      )
      return true;
    }
  }

  const filterSuggestionOnClickHandler = (value) => {
    setSearchValue(`${intl.formatMessage({id: `UTIL.FILTER.SUGGESTION.${value}`})}=`)
    setSearchValueHidden(value)
    searchInputRef?.current?.focus()
  }

  const filterRemoveOnClickHandler = (id) => {
    const newFilters = filters.filter((filter) => filter.id !== id)
    const filterIds = newFilters.map((filter) => filter.id)
    setFilters(newFilters)
    setFilterSuggestions(
      filterKeys.defaultKeyList.filter((defaultKey) => !filterIds.includes(defaultKey))
    )
    resetSearchResetOnClickHandler()
  }

  return (
    <>
      <div className='d-flex flex-column flex-row-fluid'>
        <div className='d-flex flex-row flex-wrap flex-column-fluid justify-content-start'>
          {filterSuggestions?.map((filterSuggestion, index) => (
            <div
              className={classNames('badge bg-light-primary px-3 py-1 me-2 mb-1 cursor-pointer', {
                'bg-hover-light-secondary': !loading,
                '': loading,
              })}
              key={index}
              onClick={() =>
                loading ? () => {} : filterSuggestionOnClickHandler(filterSuggestion)
              }
            >
              <div className='d-flex align-items-center'>
                <span className='text-gray-600 fw-bolder fs-8'>
                  {intl.formatMessage({id: `UTIL.FILTER.SUGGESTION.${filterSuggestion}`})}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className='d-flex flex-wrap align-items-center justify-content-end'>
          <div className='d-flex flex-column flex-row-fluid'>
            <div className='input-tag'>
              <ul
                className={classNames('input-tag-items bg-light-primary', {
                  'input-tag-items-nonempty': filters && filters.length > 0,
                  'input-tag-items-empty': !filters || filters.length === 0,
                })}
              >
                {filters
                  ?.filter((filter) => filter.editable)
                  .map((filter, index) => (
                    <div
                      className={classNames('badge bg-secondary p-3 m-1', {
                        'py-1': filter.editable,
                        'py-2': !filter.editable,
                      })}
                      key={index}
                    >
                      <div className='d-flex align-items-center'>
                        {filter.editable && (
                          <i
                            className='bi bi-x fs-3 ms-n2 fw-bold text-danger cursor-pointer text-hover-danger'
                            onClick={() => filterRemoveOnClickHandler(filter.id)}
                          ></i>
                        )}
                        <span className='text-gray-600 fs-8'>
                          {intl.formatMessage({id: `UTIL.FILTER.SUGGESTION.${filter.id}`})}
                        </span>
                        <span className='text-gray-600 fs-8'>=</span>
                        <span className='text-primary fs-8'>{filter.value}</span>
                      </div>
                    </div>
                  ))}
                <li className='tag-input'>
                  <input
                    className='bg-light-primary fs-7'
                    ref={searchInputRef}
                    type='text'
                    placeholder={intl.formatMessage({id: 'UTIL.FILTER_BY'}) + '...'}
                    disabled={!enabled || loading}
                    onKeyDown={searchBarOnKeyDownHandler}
                    onChange={searchBarOnChangeHandler}
                    value={searchValue}
                  />
                </li>
              </ul>
            </div>
          </div>
          <div className='d-flex flex-column-auto flex-center ms-2'>
            <div className='d-flex flex-row flex-column-fluid py-2'>
              <button
                type='button'
                className={classNames('btn btn-sm', {
                  'btn-primary': isValidTagList,
                  'btn-secondary': !isValidTagList,
                })}
                disabled={!enabled || loading}
                onClick={mainOnClickHandler}
              >
                <i
                  className={classNames('bi', {
                    'bi-search': isValidTagList,
                    'bi-tags': !isValidTagList,
                  })}
                ></i>
                <span className='indicator-label'>
                  {isValidTagList
                    ? intl.formatMessage({id: 'UTIL.SEARCH'})
                    : intl.formatMessage({id: 'UTIL.TAG'})}
                </span>
              </button>

              {createOnClickPath && (
                <Link
                  className='d-flex flex-row-fluid flex-center ms-2'
                  to={`${createOnClickPath}`}
                  target={createOnClickOpenNew ? '_blank' : ''}
                >
                  <button type='button' className='btn btn-sm btn-primary'>
                    <i className='bi bi-plus-lg'></i>
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'UTIL.CREATE'})}
                    </span>
                  </button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {TagInputSearchListControls}
